import { concat, find, get, includes, isEmpty, isNumber, map, values } from 'es-toolkit/compat';
import { CancerType } from 'interfaces/cancerType';
import { useEffect } from 'react';
import { Approval } from 'services/filters';

import { useRowSelectionContextOptional } from 'components/atoms/RowSelectionContext';
import { Permission } from 'interfaces/permissionOption';
import { useProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { ArrayParam, BooleanParam, JsonParam, NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { enumValuesList } from 'utils/enumValues';
import { UNSPECIFIED_FILTER_LABEL, UNSPECIFIED_FILTER_VALUE } from 'utils/unspecifiedFilterValue';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useCohortFilters from './useCohortFilters';

type StainingTypeQueryElement = string | 0;
type QueryOption = {
  value: StainingTypeQueryElement;
  text: string;
};

export const ALL_STUDIES_VALUE = '0';
export const NO_STUDY_VALUE = '-1';
export const PENDING_SLIDES_VALUE = 'pending-slides';

export const NON_SPECIFIC_STUDY_VALUES = [ALL_STUDIES_VALUE, NO_STUDY_VALUE];

const additionalStudyOptions: any = [
  { value: ALL_STUDIES_VALUE, text: 'All' },
  { value: NO_STUDY_VALUE, text: 'None' },
];

const useMainFilters = ({ enableQueries = true }: { enableQueries?: boolean } = {}) => {
  const { queries } = useCohortFilters();
  const rowSelection = useRowSelectionContextOptional();
  const [queryParams, setQueryParams] = useQueryParams({
    cohortId: StringParam,
    queryId: StringParam,
    advancedSearchDrawerOpen: BooleanParam,
    filters: JsonParam,
    features: JsonParam,
    clinicalData: JsonParam,
    compositeFilters: JsonParam,
    page: NumberParam,
    searchTerm: StringParam,
    caseIdsToInclude: ArrayParam,
    slideIdsToInclude: ArrayParam,
    pendingSlidesMode: BooleanParam,
    slidesMode: BooleanParam,
  });

  // stainTypesInStudy
  const {
    cancerTypesLoading,
    allCancerTypesLoading,
    stainTypesInStudyLoading,
    studiesLoading,
    studies,
    allCancerTypes,
    cancerTypes,
    cancerSubtypes: subTypes,
    stainTypesInStudy,
  } = useProceduresFieldsContext({ enabled: enableQueries });

  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName(undefined, {
    enabled: enableQueries,
  });
  const { hasPermission } = usePermissions();
  const canViewPendingSlides = hasPermission(Permission.ViewPendingSlides);

  const stainsOptions: QueryOption[] = [
    ...map(stainTypesInStudy, (currStain) => ({
      value: currStain,
      text: stainTypeIdToDisplayName(currStain),
    })),
    { value: UNSPECIFIED_FILTER_VALUE, text: UNSPECIFIED_FILTER_LABEL },
  ];

  const cancerSubtypes = get(subTypes, 'TISSUE')?.children;

  const cancerTypesList: CancerType[] = values(cancerTypes);
  const allCancerTypesList: CancerType[] = values(allCancerTypes);

  const cancerTypeViewOptions = map(cancerTypesList, (cancerTypeItem) => ({
    value: cancerTypeItem.id,
    text: cancerTypeItem.displayName,
  }));

  let studyOptions = null;
  let allStudyOptions = null;

  if (!isEmpty(studies)) {
    studyOptions = map(studies, (study) => ({
      value: study.id,
      text: study.name,
    }));

    allStudyOptions = concat(
      additionalStudyOptions,
      canViewPendingSlides ? [{ value: PENDING_SLIDES_VALUE, text: 'Lab lost and found slides' }] : [],
      studyOptions
    );
  }

  const approvalOptions = map(enumValuesList(Approval), (approvalValue) => ({
    value: approvalValue,
    text: approvalValue,
  }));

  const handleFilterChange = (filterKey: string, value: any) => {
    if (filterKey === 'queryId') {
      const currentQuery = find(queries, { id: value });
      const { filters, features, clinicalData, searchTerm } = currentQuery?.queryObject || {};
      setQueryParams({
        queryId: value,
        filters,
        features,
        clinicalData,
        // Reset page when switching queries
        page: 1,
        searchTerm,
      });
    } else {
      const isStudyChange = filterKey === 'studyId';
      const isPendingSlidesSelection = isStudyChange && value === PENDING_SLIDES_VALUE;
      setQueryParams(
        {
          filters: {
            ...queryParams.filters,
            [filterKey]: isPendingSlidesSelection || (!isNumber(value) && isEmpty(value)) ? undefined : value,
          },
          // Reset page when switching queries
          page: 1,
          ...(isStudyChange && { slideIdsToInclude: [], caseIdsToInclude: [] }),
          ...(isStudyChange && { pendingSlidesMode: isPendingSlidesSelection }),
          ...(isPendingSlidesSelection && { slidesMode: true }),
        },
        'replaceIn'
      );
      if (isStudyChange) {
        rowSelection?.clearSelection();
      }
    }
  };

  const isStudyIdSpecific = (studyId: string | number) => {
    return find(studies, { id: studyId }) !== undefined;
  };

  useEffect(() => {
    const currentFilters = queryParams.filters;

    if (!currentFilters || currentFilters.searchTerm === undefined) {
      return;
    }

    const { searchTerm, ...normalizedFilters } = currentFilters;
    void searchTerm;

    setQueryParams(
      {
        filters: isEmpty(normalizedFilters) ? undefined : normalizedFilters,
      },
      'replaceIn'
    );
  }, [queryParams.filters, setQueryParams]);

  useEffect(() => {
    const currentStains = queryParams.filters?.stains;
    const studyId = queryParams.filters?.studyId;

    if (studiesLoading || stainTypesInStudyLoading) return;
    if (!isStudyIdSpecific(studyId)) return;
    if (!Array.isArray(currentStains) || isEmpty(currentStains)) return;
    if (!Array.isArray(stainTypesInStudy)) return;

    const normalizedStains = currentStains.filter(
      (stain) => stain === UNSPECIFIED_FILTER_VALUE || includes(stainTypesInStudy, stain)
    );

    if (normalizedStains.length === currentStains.length) return;

    setQueryParams(
      {
        filters: {
          ...queryParams.filters,
          stains: normalizedStains.length ? normalizedStains : undefined,
        },
      },
      'replaceIn'
    );
  }, [
    isStudyIdSpecific,
    queryParams.filters,
    setQueryParams,
    stainTypesInStudy,
    stainTypesInStudyLoading,
    studiesLoading,
  ]);

  return {
    allStudyOptions,
    studyOptions,
    approvalOptions,
    stainsOptions,
    cancerTypesList,
    allCancerTypesList,
    cancerTypeViewOptions,
    cancerSubtypes,
    handleFilterChange,
    cancerTypesLoading,
    allCancerTypesLoading,
    stainTypesInStudyLoading,
    isLoadingStainTypeOptions,
    studiesLoading,
    pendingSlidesMode: queryParams.pendingSlidesMode,
    isStudyIdSpecific,
  };
};

export default useMainFilters;
